// NotFoundPage — friendly 404 with helpful links back into the site. Routed at
// the catch-all "*" path (last route in App.js).

import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Home, Search, Compass } from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";

const HELPFUL_LINKS = [
  { to: "/", label: "Home", desc: "Back to the start", icon: Home },
  {
    to: "/seo-audit",
    label: "Free SEO Audit",
    desc: "Score your website now",
    icon: Search,
  },
  {
    to: "/case-studies",
    label: "Case Studies",
    desc: "See real client results",
    icon: Compass,
  },
];

const POPULAR = [
  { to: "/cro", label: "Conversion Optimization" },
  { to: "/content-marketing", label: "Content Marketing" },
  { to: "/web-design", label: "Web Design" },
  { to: "/link-building", label: "Link Building" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact" },
];

export default function NotFoundPage() {
  return (
    <div className="bg-white text-charcoal min-h-screen flex flex-col">
      <Helmet>
        <title>Page Not Found | Lumotis</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="The page you're looking for doesn't exist. Explore Lumotis services, case studies, and our free SEO audit tool." />
      </Helmet>

      <Navbar />

      <main className="flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-70 pointer-events-none" />
        <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/10 via-transparent to-transparent pointer-events-none" />

        <div className="container relative pt-28 md:pt-36 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <p className="font-display text-[5rem] sm:text-[7rem] leading-none font-black text-electric/20">
              404
            </p>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-charcoal mt-2 text-balance">
              This page took a wrong turn.
            </h1>
            <p className="mt-5 text-lg text-zinc-600 leading-relaxed">
              The link may be broken or the page may have moved. Let's get you
              back to something useful.
            </p>

            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold hover:bg-electric-400 hover:-translate-y-0.5 transition-all shadow-lg rounded-xl"
            >
              Back to home <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <div className="mt-14 grid sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 max-w-4xl">
            {HELPFUL_LINKS.map((l, i) => {
              const Icon = l.icon;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className="group bg-white p-7 hover:bg-charcoal hover:text-white transition-colors"
                  data-testid={`notfound-link-${i}`}
                >
                  <span className="w-11 h-11 grid place-items-center bg-electric text-white mb-5">
                    <Icon className="w-5 h-5" strokeWidth={2.2} />
                  </span>
                  <h2 className="font-display text-lg font-bold">{l.label}</h2>
                  <p className="mt-2 text-sm text-zinc-600 group-hover:text-zinc-300">
                    {l.desc}
                  </p>
                </Link>
              );
            })}
          </div>

          <div className="mt-12 max-w-4xl">
            <p className="text-overline text-zinc-500 mb-4">Popular pages</p>
            <div className="flex flex-wrap gap-2">
              {POPULAR.map((p) => (
                <Link
                  key={p.to}
                  to={p.to}
                  className="inline-flex items-center border border-zinc-200 rounded-full px-4 py-2 text-sm font-semibold text-charcoal hover:border-electric hover:text-electric transition-colors"
                >
                  {p.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
